/**
 * useActiveChunkIdTracker
 * -----------------------
 *
 * The Flashcard Library tab needs the **SecureContentChunkId (Guid)** of the
 * chunk the student is currently viewing. Unfortunately, none of the
 * student-facing content endpoints used by the three renderers today return
 * that Guid alongside the chunk content — they return `ChunkIndex` (an int)
 * or page numbers instead. The *one* student-authorized endpoint we call
 * that *does* surface `secureContentChunkId` is `/search-in-book`, and its
 * results map { chapterIndex | pageNumber } → secureContentChunkId.
 *
 * This hook is a minimal client-side cache of that mapping:
 *
 *   • `harvestSearchResponse(response)` — feed it the result of
 *     `booksService.searchInBook(...)`; it learns
 *     chapterIndex/pageNumber → secureContentChunkId mappings. Cheap, idempotent,
 *     and does not trigger network calls of its own.
 *
 *   • `harvestChapterList(chapters, keyFn)` — pre-populate the mapping from a
 *     chapter-list / pdf-chunks-metadata response that carries
 *     `secureContentChunkId` per entry. Called once at book-open time so the
 *     Library tab resolves immediately, without waiting for a search.
 *
 *   • `resolveForKey(key)` — read-only lookup. Returns the chunkId for the
 *     given location key if it has been harvested before; otherwise `null`.
 *
 *   • The returned state is a `Map`, which makes it trivial for renderer-level
 *     hooks to depend on the current location key and re-compute an
 *     `activeChunkId` every time the location changes *or* a new harvest
 *     happens.
 *
 * Known limitation: the EPUB.js renderer uses `/epubjs/manifest` (not
 * `/chapter-list`) which does not yet carry `secureContentChunkId` per spine
 * item, and the PDF renderer has no chunkId-aware metadata endpoint yet. For
 * those two paths the tracker still relies on `/search-in-book`; the
 * CustomHtml path pre-populates via `harvestChapterList` at book-open time.
 */
import { useCallback, useRef, useState } from 'react';
import type { BookSearchResultDto } from '../../types/search';

/**
 * Location keys are normalized to strings so EPUB (chapterIndex) and PDF
 * (pageNumber) can share the same Map. EPUB keys: `"ch:${chapterIndex}"`.
 * PDF keys: `"pg:${pageNumber}"`. Callers compose keys via the helpers below.
 */
export type ChunkKeyKind = 'chapter' | 'page';

export function makeChunkKey(kind: ChunkKeyKind, value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return kind === 'chapter' ? `ch:${value}` : `pg:${value}`;
}

export interface ActiveChunkIdTracker {
  /** The full known mapping. Primarily for debugging / React-dep-triggering. */
  mapping: Map<string, string>;
  /** Record any chapterIndex/pageNumber → chunkId pairs from a search response. */
  harvestSearchResponse: (response: BookSearchResultDto | null | undefined) => void;
  /**
   * Pre-populate the tracker from a chapter-list / pdf-chunks-metadata response.
   * Each renderer supplies its own `keyFn` so EPUB (chapterIndex) and PDF
   * (pageNumber) can key into the same Map shape used by `resolveForKey`.
   * Items missing a `secureContentChunkId` are skipped.
   */
  harvestChapterList: <T extends { secureContentChunkId?: string | null }>(
    chapters: ReadonlyArray<T> | null | undefined,
    keyFn: (chapter: T, index: number) => string | null
  ) => void;
  /** Lookup by composed key. Returns `null` when unknown. */
  resolveForKey: (key: string | null | undefined) => string | null;
}

export function useActiveChunkIdTracker(): ActiveChunkIdTracker {
  const [mapping, setMapping] = useState<Map<string, string>>(() => new Map());
  // Ref mirrors state so resolveForKey can be stable across renders.
  const mappingRef = useRef(mapping);
  mappingRef.current = mapping;

  const harvestSearchResponse = useCallback(
    (response: BookSearchResultDto | null | undefined) => {
      if (!response || !Array.isArray(response.results) || response.results.length === 0) {
        return;
      }
      let dirty = false;
      const next = new Map(mappingRef.current);
      for (const r of response.results) {
        const chunkId = r.secureContentChunkId;
        if (!chunkId) continue;
        if (typeof r.chapterIndex === 'number' && r.chapterIndex >= 0) {
          const key = `ch:${r.chapterIndex}`;
          if (next.get(key) !== chunkId) {
            next.set(key, chunkId);
            dirty = true;
          }
        }
        if (typeof r.pageNumber === 'number' && r.pageNumber >= 0) {
          const key = `pg:${r.pageNumber}`;
          if (next.get(key) !== chunkId) {
            next.set(key, chunkId);
            dirty = true;
          }
        }
      }
      if (dirty) {
        setMapping(next);
      }
    },
    []
  );

  const harvestChapterList = useCallback(
    <T extends { secureContentChunkId?: string | null }>(
      chapters: ReadonlyArray<T> | null | undefined,
      keyFn: (chapter: T, index: number) => string | null
    ) => {
      if (!Array.isArray(chapters) || chapters.length === 0) {
        return;
      }
      let dirty = false;
      const next = new Map(mappingRef.current);
      chapters.forEach((chapter, index) => {
        const chunkId = chapter?.secureContentChunkId;
        if (!chunkId) return;
        const key = keyFn(chapter, index);
        if (!key) return;
        if (next.get(key) !== chunkId) {
          next.set(key, chunkId);
          dirty = true;
        }
      });
      if (dirty) {
        setMapping(next);
      }
    },
    []
  );

  const resolveForKey = useCallback((key: string | null | undefined): string | null => {
    if (!key) return null;
    return mappingRef.current.get(key) ?? null;
  }, []);

  return { mapping, harvestSearchResponse, harvestChapterList, resolveForKey };
}
